// WbYardDetailPage — /weighbridge/settings/yards/:enc_id (and /new).
//
// Phase C2 Group 1. Create + edit form for a single yard. Delete lives
// here too; the server returns 409 when the yard has dependent
// weighbridges and we surface that inline.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useCreateYard,
  useDeleteYard,
  useUpdateYard,
  useWbYard,
  type CreateYardInput,
} from '../hooks/useWbYards';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbYardDetailPage.module.css';

// Hard-coded common IANA timezone list. No company-timezone concept
// exists in the client yet — see brief for the rationale.
const TIMEZONES: ReadonlyArray<string> = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
];

interface FormState {
  name: string;
  code: string;
  address: string;
  timezone: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  code: '',
  address: '',
  timezone: 'Europe/London',
  is_active: true,
};

export function WbYardDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || enc_id === undefined;

  const yardQuery = useWbYard(isNew ? undefined : enc_id);
  const createMut = useCreateYard();
  const updateMut = useUpdateYard(isNew ? '' : (enc_id as string));
  const deleteMut = useDeleteYard(isNew ? '' : (enc_id as string));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Hydrate the form once the GET resolves.
  useEffect(() => {
    if (isNew) return;
    const yard = yardQuery.data?.yard;
    if (!yard) return;
    setForm({
      name: yard.name,
      code: yard.code,
      address: yard.address ?? '',
      timezone: yard.timezone,
      is_active: yard.is_active,
    });
  }, [isNew, yardQuery.data]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs['name'] = 'Name is required.';
    if (!form.code.trim()) errs['code'] = 'Code is required.';
    if (form.code.length > 20) errs['code'] = 'Code must be 20 characters or fewer.';
    if (form.name.length > 200) errs['name'] = 'Name must be 200 characters or fewer.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function buildBody(): CreateYardInput {
    return {
      name: form.name.trim(),
      code: form.code.trim(),
      address: form.address.trim() ? form.address.trim() : null,
      timezone: form.timezone,
      is_active: form.is_active,
    };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    const body = buildBody();
    if (isNew) {
      createMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/settings/yards'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    } else {
      updateMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/settings/yards'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    }
  }

  function handleDeleteConfirm() {
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/weighbridge/settings/yards');
      },
      onError: (err) => {
        setConfirmOpen(false);
        setError(extractErrorMessage(err));
      },
    });
  }

  const isSaving = createMut.isPending || updateMut.isPending;
  const isLoadingExisting = !isNew && yardQuery.isLoading;

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Yard' : form.name || 'Edit Yard'}
        subtitle={isNew ? 'Create a new yard' : 'Edit yard details'}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/settings/yards')}
          >
            Back to Yards
          </Button>
        }
      />

      {isLoadingExisting ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading yard" />
          </div>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <Card>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Name</span>
                <input
                  type="text"
                  required
                  maxLength={200}
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
                {fieldErrors['name'] ? (
                  <span className={styles.fieldError}>{fieldErrors['name']}</span>
                ) : null}
              </label>

              <label className={styles.field}>
                <span>Code</span>
                <input
                  type="text"
                  required
                  maxLength={20}
                  value={form.code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, code: e.target.value }))
                  }
                  onBlur={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      code: e.target.value.toUpperCase(),
                    }))
                  }
                />
                {fieldErrors['code'] ? (
                  <span className={styles.fieldError}>{fieldErrors['code']}</span>
                ) : null}
              </label>

              <label className={styles.field}>
                <span>Timezone</span>
                <select
                  value={form.timezone}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, timezone: e.target.value }))
                  }
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </label>

              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                  }
                />
                <span>Active</span>
              </label>
            </div>

            <label className={styles.field} style={{ marginTop: '1rem' }}>
              <span>Address</span>
              <textarea
                rows={3}
                maxLength={2000}
                value={form.address}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, address: e.target.value }))
                }
                placeholder="Optional"
              />
            </label>
          </Card>

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.actions}>
            <div>
              {!isNew ? (
                <Button
                  type="button"
                  variant="danger"
                  icon={Trash2}
                  onClick={() => setConfirmOpen(true)}
                  disabled={isSaving}
                >
                  Delete
                </Button>
              ) : null}
            </div>
            <div className={styles.rightActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/weighbridge/settings/yards')}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isSaving}>
                {isNew ? 'Create Yard' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Delete yard?"
        message={
          <p>
            Delete <strong>{form.name || 'this yard'}</strong>? This cannot be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmOpen(false)}
        loading={deleteMut.isPending}
      />
    </div>
  );
}
